import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BusinessProvider } from './context/BusinessContext';
import { ModalProvider } from './context/ModalContext';
import DevEnvBanner from './components/DevEnvBanner';
import LoginScreen from './screens/LoginScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import DashboardScreen from './screens/DashboardScreen';
import FacilitySetupScreen from './screens/FacilitySetupScreen';
import CourtsPricingScreen from './screens/CourtsPricingScreen';
import SuperAdminScreen from './screens/SuperAdminScreen';
import BookingsScreen from './screens/BookingsScreen';
import PaymentsScreen from './screens/PaymentsScreen';
import GoLiveScreen from './screens/GoLiveScreen';
import MembersScreen from './screens/MembersScreen';
import WidgetDemoScreen from './screens/WidgetDemoScreen';
import './styles/index.css';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isSuperAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading" style={{ minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperAdmin) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// This is deliberately separate from ProtectedRoute. Hiding the sidebar item is
// only a convenience; the route itself must not render for venue-level admins.
function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isSuperAdmin, loading } = useAuth();

  if (loading) {
    return <div className="loading" style={{ minHeight: '100vh' }}><div className="spinner" /></div>;
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

// Public Route wrapper (redirects to dashboard if already logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading" style={{ minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/widget-demo" element={<WidgetDemoScreen />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginScreen />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordScreen />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bookings"
        element={
          <ProtectedRoute>
            <BookingsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/facility-setup"
        element={
          <ProtectedRoute>
            <FacilitySetupScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courts-pricing"
        element={
          <ProtectedRoute>
            <CourtsPricingScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/superadmin"
        element={
          <SuperAdminRoute>
            <SuperAdminScreen />
          </SuperAdminRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute>
            <PaymentsScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/go-live"
        element={
          <ProtectedRoute>
            <GoLiveScreen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/members"
        element={
          <ProtectedRoute>
            <MembersScreen />
          </ProtectedRoute>
        }
      />
      {/* Redirect old routes to appropriate pages */}
      <Route path="/business-profile" element={<Navigate to="/facility-setup" replace />} />
      <Route path="/courts" element={<Navigate to="/courts-pricing" replace />} />
      <Route path="/pricing" element={<Navigate to="/courts-pricing" replace />} />
      <Route path="/policies" element={<Navigate to="/facility-setup" replace />} />
      <Route path="/photos" element={<Navigate to="/facility-setup" replace />} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BusinessProvider>
          <ModalProvider>
            <DevEnvBanner />
            <AppRoutes />
          </ModalProvider>
        </BusinessProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
